import {
  createBusinessUnitSchema,
  createLegalEntitySchema,
  orgCodeSchema,
  orgNameSchema,
  PERMISSIONS,
} from "@procuremate/shared";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "@/app/require-permission";
import { FormError } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/select-field";
import { TextField } from "@/components/ui/text-field";
import { adminErrorMessage } from "./admin-error";
import { Card } from "./admin-layout";
import {
  useCreateBusinessUnit,
  useCreateLegalEntity,
  useOrg,
  useUpdateBusinessUnit,
  useUpdateLegalEntity,
} from "./api";
import { ActiveBadge } from "./status-badge";

/**
 * Legal entities and business units: the scopes buyers are assigned to (ADR-0012).
 * Records are deactivated, never deleted (role assignments and requests refer to them).
 */
export function OrgPage() {
  const { t } = useTranslation();
  const canManage = useHasPermission(PERMISSIONS.ORG_MANAGE);
  const org = useOrg();
  const updateLe = useUpdateLegalEntity();
  const updateBu = useUpdateBusinessUnit();
  const legalEntities = org.data?.legalEntities ?? [];
  const businessUnits = org.data?.businessUnits ?? [];
  const leName = new Map(legalEntities.map((le) => [le.id, `${le.code} — ${le.name}`]));
  const parentOptions = legalEntities
    .filter((le) => le.active)
    .map((le) => ({ value: le.id, label: `${le.code} — ${le.name}` }));

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold text-primary">{t("admin.org.title")}</h1>
      {org.isError ? <FormError message={t("auth.genericError")} /> : null}

      <Card title={t("admin.org.legalEntities")}>
        <OrgTable
          kind="le"
          caption={t("admin.org.legalEntities")}
          rows={legalEntities.map((le) => ({ ...le, parentId: null, parent: null }))}
          canManage={canManage}
          pending={updateLe.isPending}
          error={updateLe.error}
          onToggle={(row) => updateLe.mutate({ id: row.id, active: !row.active })}
          onSave={(row, values, done) =>
            updateLe.mutate({ id: row.id, code: values.code, name: values.name }, { onSuccess: done })
          }
        />
        {canManage ? <CreateLegalEntityForm /> : null}
      </Card>

      <Card title={t("admin.org.businessUnits")}>
        <OrgTable
          kind="bu"
          caption={t("admin.org.businessUnits")}
          rows={businessUnits.map((bu) => ({
            ...bu,
            parentId: bu.legalEntityId,
            parent: bu.legalEntityId ? (leName.get(bu.legalEntityId) ?? null) : null,
          }))}
          parentOptions={parentOptions}
          canManage={canManage}
          pending={updateBu.isPending}
          error={updateBu.error}
          onToggle={(row) => updateBu.mutate({ id: row.id, active: !row.active })}
          onSave={(row, values, done) =>
            updateBu.mutate(
              { id: row.id, code: values.code, name: values.name, legalEntityId: values.parentId },
              { onSuccess: done },
            )
          }
        />
        {canManage ? <CreateBusinessUnitForm legalEntities={legalEntities.filter((le) => le.active)} /> : null}
      </Card>
    </div>
  );
}

interface OrgRow {
  id: string;
  code: string;
  name: string;
  active: boolean;
  parentId: string | null;
  parent: string | null;
}

interface OrgEditValues {
  code: string;
  name: string;
  parentId: string | null;
}

function OrgTable({
  kind,
  caption,
  rows,
  parentOptions,
  canManage,
  pending,
  error,
  onToggle,
  onSave,
}: {
  kind: "le" | "bu";
  caption: string;
  rows: OrgRow[];
  /** Business units only: the legal entities a unit can belong to. */
  parentOptions?: { value: string; label: string }[];
  canManage: boolean;
  pending: boolean;
  error: unknown;
  onToggle: (row: OrgRow) => void;
  onSave: (row: OrgRow, values: OrgEditValues, done: () => void) => void;
}) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  // Focus returns to the row's Edit button after the commit that re-mounts it (a
  // requestAnimationFrame could run before it and find no button — the old flaky test).
  const [returnTo, setReturnTo] = useState<string | null>(null);
  useEffect(() => {
    if (editingId !== null || returnTo === null) return;
    editButtons.current.get(returnTo)?.focus();
    setReturnTo(null);
  }, [editingId, returnTo]);
  const showParent = parentOptions !== undefined;
  const columns = 3 + (showParent ? 1 : 0) + (canManage ? 1 : 0);

  function closeEditor(id: string) {
    // Return focus to the Edit button of this row, so keyboard users keep their place.
    setReturnTo(id);
    setEditingId(null);
  }

  if (rows.length === 0) return <p className="text-fg-muted">{t("admin.org.empty")}</p>;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="border-b border-border text-fg-muted">
            <tr>
              <th scope="col" className="hidden py-2 pr-4 font-semibold sm:table-cell">
                {t("admin.org.code")}
              </th>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.org.name")}
              </th>
              {showParent ? (
                <th scope="col" className="hidden py-2 pr-4 font-semibold md:table-cell">
                  {t("admin.org.legalEntity")}
                </th>
              ) : null}
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.org.state")}
              </th>
              {canManage ? (
                <th scope="col" className="py-2 font-semibold">
                  <span className="sr-only">{t("admin.org.actions")}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) =>
              editingId === row.id ? (
                <tr key={row.id} className="border-b border-border last:border-0">
                  <td colSpan={columns} className="py-3">
                    <OrgEditForm
                      idPrefix={`${kind}-edit-${row.id}`}
                      row={row}
                      parentOptions={parentOptions}
                      pending={pending}
                      onCancel={() => closeEditor(row.id)}
                      onSave={(values) => onSave(row, values, () => closeEditor(row.id))}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={row.id} className="border-b border-border last:border-0">
                  <td className="hidden py-3 pr-4 font-mono text-xs font-semibold sm:table-cell">{row.code}</td>
                  <td className="py-3 pr-4">
                    <span>{row.name}</span>
                    {/* Phones: the code sits under the name instead of in its own column. */}
                    <div className="font-mono text-xs text-fg-muted sm:hidden">{row.code}</div>
                  </td>
                  {showParent ? (
                    <td className="hidden py-3 pr-4 text-fg-muted md:table-cell">{row.parent ?? "—"}</td>
                  ) : null}
                  <td className="py-3 pr-4">
                    <ActiveBadge active={row.active} />
                  </td>
                  {canManage ? (
                    <td className="py-3 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          ref={(el) => {
                            if (el) editButtons.current.set(row.id, el);
                            else editButtons.current.delete(row.id);
                          }}
                          variant="ghost"
                          className="h-8 px-2"
                          disabled={editingId !== null}
                          aria-label={t("admin.org.editLabel", { name: row.name })}
                          onClick={() => setEditingId(row.id)}
                        >
                          {t("admin.org.edit")}
                        </Button>
                        <Button
                          variant="ghost"
                          className="h-8 px-2"
                          disabled={pending || editingId !== null}
                          aria-label={t(row.active ? "admin.org.deactivateLabel" : "admin.org.activateLabel", {
                            name: row.name,
                          })}
                          onClick={() => onToggle(row)}
                        >
                          {row.active ? t("admin.org.deactivate") : t("admin.org.activate")}
                        </Button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      <FormError message={adminErrorMessage(error, t, "admin.errors.codeExists")} />
    </>
  );
}

/** Inline editor for one legal entity / business unit row. */
function OrgEditForm({
  idPrefix,
  row,
  parentOptions,
  pending,
  onCancel,
  onSave,
}: {
  idPrefix: string;
  row: OrgRow;
  parentOptions?: { value: string; label: string }[] | undefined;
  pending: boolean;
  onCancel: () => void;
  onSave: (values: OrgEditValues) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState(row.code);
  const [name, setName] = useState(row.name);
  const [parentId, setParentId] = useState(row.parentId ?? "");
  const [errors, setErrors] = useState<{ code?: string; name?: string }>({});
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const codeOk = orgCodeSchema.safeParse(code);
    const nameOk = orgNameSchema.safeParse(name);
    if (!codeOk.success || !nameOk.success) {
      setErrors({
        code: codeOk.success ? undefined : t("admin.org.codeHint"),
        name: nameOk.success ? undefined : t("admin.org.nameRequired"),
      });
      return;
    }
    setErrors({});
    onSave({ code: codeOk.data, name: nameOk.data, parentId: parentId || null });
  }

  return (
    <form
      onSubmit={onSubmit}
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
      }}
      noValidate
      aria-label={t("admin.org.editLabel", { name: row.name })}
      className="grid gap-3 rounded-md bg-bg p-3 md:grid-cols-[10rem_1fr_1fr_auto] md:items-start"
    >
      <TextField
        ref={firstField}
        id={`${idPrefix}-code`}
        label={t("admin.org.code")}
        value={code}
        error={errors.code}
        onChange={(e) => {
          setCode(e.target.value);
          setErrors((x) => ({ ...x, code: undefined }));
        }}
      />
      <TextField
        id={`${idPrefix}-name`}
        label={t("admin.org.name")}
        value={name}
        error={errors.name}
        onChange={(e) => {
          setName(e.target.value);
          setErrors((x) => ({ ...x, name: undefined }));
        }}
      />
      {parentOptions ? (
        <SelectField
          id={`${idPrefix}-parent`}
          label={t("admin.org.legalEntityOptional")}
          placeholder={t("admin.org.noLegalEntity")}
          value={parentId}
          options={parentOptions}
          onChange={(e) => setParentId(e.target.value)}
        />
      ) : (
        <span className="hidden md:block" />
      )}
      <div className="flex gap-2 md:mt-7">
        <Button type="submit" variant="secondary" disabled={pending}>
          {t("admin.save")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("admin.cancel")}
        </Button>
      </div>
    </form>
  );
}

function CreateLegalEntityForm() {
  const { t } = useTranslation();
  const create = useCreateLegalEntity();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<{ code?: string; name?: string }>({});

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createLegalEntitySchema.safeParse({ code, name });
    if (!parsed.success) {
      const issues = parsed.error.issues.map((i) => i.path[0]);
      setErrors({
        code: issues.includes("code") ? t("admin.org.codeHint") : undefined,
        name: issues.includes("name") ? t("admin.org.nameRequired") : undefined,
      });
      return;
    }
    setErrors({});
    create.mutate(parsed.data, {
      onSuccess: () => {
        setCode("");
        setName("");
      },
    });
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mt-6 border-t border-border pt-6">
      <h3 className="mb-4 text-base font-semibold">{t("admin.org.addLegalEntity")}</h3>
      <div className="grid gap-4 sm:grid-cols-[12rem_1fr_auto] sm:items-start">
        <TextField
          id="le-code"
          label={t("admin.org.code")}
          value={code}
          error={errors.code}
          onChange={(e) => {
            setCode(e.target.value);
            setErrors((x) => ({ ...x, code: undefined }));
          }}
        />
        <TextField
          id="le-name"
          label={t("admin.org.name")}
          value={name}
          error={errors.name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((x) => ({ ...x, name: undefined }));
          }}
        />
        <Button type="submit" variant="secondary" className="sm:mt-7" disabled={create.isPending}>
          {t("admin.org.add")}
        </Button>
      </div>
      <FormError message={adminErrorMessage(create.error, t, "admin.errors.codeExists")} />
    </form>
  );
}

function CreateBusinessUnitForm({ legalEntities }: { legalEntities: { id: string; code: string; name: string }[] }) {
  const { t } = useTranslation();
  const create = useCreateBusinessUnit();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [legalEntityId, setLegalEntityId] = useState("");
  const [errors, setErrors] = useState<{ code?: string; name?: string }>({});

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createBusinessUnitSchema.safeParse({ code, name, legalEntityId: legalEntityId || null });
    if (!parsed.success) {
      const issues = parsed.error.issues.map((i) => i.path[0]);
      setErrors({
        code: issues.includes("code") ? t("admin.org.codeHint") : undefined,
        name: issues.includes("name") ? t("admin.org.nameRequired") : undefined,
      });
      return;
    }
    setErrors({});
    create.mutate(parsed.data, {
      onSuccess: () => {
        setCode("");
        setName("");
        setLegalEntityId("");
      },
    });
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mt-6 border-t border-border pt-6">
      <h3 className="mb-4 text-base font-semibold">{t("admin.org.addBusinessUnit")}</h3>
      <div className="grid gap-4 md:grid-cols-[10rem_1fr_1fr_auto] md:items-start">
        <TextField
          id="bu-code"
          label={t("admin.org.code")}
          value={code}
          error={errors.code}
          onChange={(e) => {
            setCode(e.target.value);
            setErrors((x) => ({ ...x, code: undefined }));
          }}
        />
        <TextField
          id="bu-name"
          label={t("admin.org.name")}
          value={name}
          error={errors.name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((x) => ({ ...x, name: undefined }));
          }}
        />
        <SelectField
          id="bu-le"
          label={t("admin.org.legalEntityOptional")}
          placeholder={t("admin.org.noLegalEntity")}
          value={legalEntityId}
          options={legalEntities.map((le) => ({ value: le.id, label: `${le.code} — ${le.name}` }))}
          onChange={(e) => setLegalEntityId(e.target.value)}
        />
        <Button type="submit" variant="secondary" className="md:mt-7" disabled={create.isPending}>
          {t("admin.org.add")}
        </Button>
      </div>
      <FormError message={adminErrorMessage(create.error, t, "admin.errors.codeExists")} />
    </form>
  );
}
